import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { favouritesService } from '../../services/supabaseService';
import { useAuth } from '../../contexts/AuthContext';
import { Search, Heart, MessageCircle, Check, Loader2 } from 'lucide-react';

const PAGE_SIZE = 48;

const DashboardFindPage = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [allProviders, setAllProviders] = useState([]);
  const [favouriteIds, setFavouriteIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [filters, setFilters] = useState({
    friendType: 'both',
    gender: '',
    city: '',
    search: '',
  });

  const isPremium = useMemo(() => {
    const s = userProfile?.subscriptionStatus ?? userProfile?.subscription_status ?? 'free';
    return ['premium', 'active', 'trialing'].includes(String(s).toLowerCase());
  }, [userProfile?.subscriptionStatus, userProfile?.subscription_status]);
  const isProvider = userProfile?.role === 'service_provider';

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }
    const load = async () => {
      setLoading(true);
      setPage(0);
      let q = supabase
        .from('user_profiles')
        .select('id, username, display_name, avatar_url, city, gender, is_verified, subscription_status, is_dummy, service_provider_profiles(categories)')
        .eq('hide_profile', false)
        .not('is_blocked', 'eq', true)
        .or('role.eq.service_provider,is_dummy.eq.true');
      if (filters.gender) q = q.eq('gender', filters.gender);
      if (filters.city?.trim()) q = q.ilike('city', `%${filters.city.trim()}%`);
      q = q.range(0, PAGE_SIZE - 1).order('created_at', { ascending: false });
      const { data, error, count } = await q;
      if (!error) {
        setAllProviders(data || []);
        setHasMore((data?.length || 0) === PAGE_SIZE);
      }
      setLoading(false);
    };
    load();
  }, [filters.gender, filters.city]);

  const filteredProviders = useMemo(() => {
    let list = allProviders;
    if (filters.friendType !== 'both') {
      list = list.filter((p) => {
        const cats = p.service_provider_profiles?.categories || [];
        if (cats.length === 0) return true;
        if (filters.friendType === 'hire_a_friend') return cats.includes('hire_a_friend') || cats.includes('both');
        if (filters.friendType === 'fwb') return cats.includes('fwb') || cats.includes('both');
        return true;
      });
    }
    if (filters.search?.trim()) {
      const term = filters.search.trim().toLowerCase();
      list = list.filter(
        (p) =>
          (p.display_name || '').toLowerCase().includes(term) ||
          (p.username || '').toLowerCase().includes(term) ||
          (p.city || '').toLowerCase().includes(term)
      );
    }
    return list;
  }, [allProviders, filters.friendType, filters.search]);

  useEffect(() => {
    if (!user?.id || filteredProviders.length === 0) return;
    Promise.all(filteredProviders.map((p) => favouritesService.isFavourite(user.id, p.id)))
      .then((results) => {
        const ids = new Set(filteredProviders.filter((_, i) => results[i]).map((p) => p.id));
        setFavouriteIds(ids);
      })
      .catch(() => {});
  }, [user?.id, filteredProviders]);

  const loadMore = async () => {
    if (!supabase || loadingMore || !hasMore) return;
    setLoadingMore(true);
    const offset = allProviders.length;
    let q = supabase
      .from('user_profiles')
      .select('id, username, display_name, avatar_url, city, gender, is_verified, subscription_status, is_dummy, service_provider_profiles(categories)')
      .eq('hide_profile', false)
      .not('is_blocked', 'eq', true)
      .or('role.eq.service_provider,is_dummy.eq.true');
    if (filters.gender) q = q.eq('gender', filters.gender);
    if (filters.city?.trim()) q = q.ilike('city', `%${filters.city.trim()}%`);
    const { data, error } = await q.range(offset, offset + PAGE_SIZE - 1).order('created_at', { ascending: false });
    if (!error && data?.length) {
      setAllProviders((prev) => [...prev, ...data]);
      setHasMore(data.length === PAGE_SIZE);
    } else {
      setHasMore(false);
    }
    setLoadingMore(false);
  };

  const requirePremium = (action) => {
    if (isProvider) return;
    if (!isPremium) {
      navigate('/payment');
      return true;
    }
    return false;
  };

  return (
    <div>
      <h1 className="font-heading font-bold text-2xl text-white mb-4">Find a Friend</h1>
      <div className="mb-6 flex flex-wrap gap-4">
        <select
          value={filters.friendType}
          onChange={(e) => setFilters((f) => ({ ...f, friendType: e.target.value }))}
          className="px-3 py-2 rounded-xl border border-white/20 bg-slate-800 text-slate-200"
        >
          <option value="both">Hire a friend / FWB / Both</option>
          <option value="hire_a_friend">Hire a friend</option>
          <option value="fwb">Friendship with Benefits</option>
        </select>
        <select
          value={filters.gender}
          onChange={(e) => setFilters((f) => ({ ...f, gender: e.target.value }))}
          className="px-3 py-2 rounded-xl border border-white/20 bg-slate-800 text-slate-200"
        >
          <option value="">Gender</option>
          <option value="male">Male</option>
          <option value="female">Female</option>
          <option value="other">Other</option>
        </select>
        <input
          type="text"
          placeholder="City"
          value={filters.city}
          onChange={(e) => setFilters((f) => ({ ...f, city: e.target.value }))}
          className="px-3 py-2 rounded-xl border border-white/20 bg-slate-800 text-slate-200 w-40 placeholder:text-slate-500"
        />
        <input
          type="text"
          placeholder="Search name or city..."
          value={filters.search}
          onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
          className="px-3 py-2 rounded-xl border border-white/20 bg-slate-800 text-slate-200 w-48 placeholder:text-slate-500"
        />
      </div>
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-brand-pink border-t-transparent" />
        </div>
      ) : filteredProviders.length === 0 ? (
        <div className="p-12 rounded-2xl bg-slate-800/50 border border-white/10 text-center text-slate-400">
          <Search className="h-12 w-12 mx-auto mb-4 text-slate-500" />
          <p>No profiles match your filters. Try changing filters or search.</p>
        </div>
      ) : (
        <>
          <p className="text-slate-400 text-sm mb-4">
            Showing {filteredProviders.length} profile{filteredProviders.length !== 1 ? 's' : ''}
            {filteredProviders.length !== allProviders.length && ` (${allProviders.length} loaded)`}.
            Only friends who offer services appear here; regular members are not listed.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProviders.map((p) => {
              const isFav = favouriteIds.has(p.id);
              const toggleFav = async (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (requirePremium('favourite')) return;
                if (!user?.id) return;
                try {
                  if (isFav) {
                    await favouritesService.remove(user.id, p.id);
                    setFavouriteIds((prev) => {
                      const next = new Set(prev);
                      next.delete(p.id);
                      return next;
                    });
                  } else {
                    await favouritesService.add(user.id, p.id);
                    setFavouriteIds((prev) => new Set(prev).add(p.id));
                  }
                } catch (err) {
                  console.error(err);
                }
              };
              return (
                <div
                  key={p.id}
                  className="rounded-2xl overflow-hidden bg-slate-800/50 border border-white/10 hover:border-white/20 transition-all"
                >
                  <Link to={`/dashboard/profile/view/${p.id}`} className="block">
                    <div className="aspect-[3/4] bg-slate-700 relative overflow-hidden">
                      <img
                        src={p.avatar_url || '/default-avatar.svg'}
                        alt={p.display_name || 'Profile'}
                        className="w-full h-full object-cover"
                      />
                      {p.is_verified && (
                        <div className="absolute top-2 right-2 w-8 h-8 rounded-full bg-brand-pink flex items-center justify-center">
                          <Check className="h-4 w-4 text-white" />
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-white">
                        {p.display_name || p.username || 'Friend'}
                      </h3>
                      {p.city && <p className="text-sm text-slate-400">{p.city}</p>}
                    </div>
                  </Link>
                  <div className="px-4 pb-4 flex gap-2">
                    {isPremium || isProvider ? (
                      <Link
                        to={`/dashboard/profile/view/${p.id}`}
                        className="flex-1 flex items-center justify-center gap-1 py-2 rounded-xl bg-brand-pink text-white text-sm font-medium hover:bg-brand-pink-dark transition-colors"
                      >
                        <MessageCircle className="h-4 w-4" />
                        Message
                      </Link>
                    ) : (
                      <button
                        type="button"
                        onClick={() => requirePremium('message')}
                        className="flex-1 flex items-center justify-center gap-1 py-2 rounded-xl bg-amber-500 text-white text-sm font-medium hover:bg-amber-600"
                      >
                        Upgrade to message
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={toggleFav}
                      className={`p-2 rounded-xl border transition-colors ${
                        isFav
                          ? 'border-brand-pink bg-brand-pink/20 text-brand-pink'
                          : 'border-white/20 text-slate-400 hover:bg-white/10'
                      }`}
                      aria-label={isFav ? 'Remove from favourites' : 'Add to favourites'}
                    >
                      <Heart className={`h-4 w-4 ${isFav ? 'fill-current' : ''}`} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          {!loading && hasMore && allProviders.length >= PAGE_SIZE && (
            <div className="mt-8 flex justify-center">
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                className="px-6 py-3 rounded-xl bg-slate-800 border border-white/20 text-white font-medium hover:bg-slate-700 disabled:opacity-50 flex items-center gap-2"
              >
                {loadingMore ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Load more
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DashboardFindPage;
